/**
 * Storefront chat — merchant settings.
 *
 * Loads /merchant/chatbot/settings (lazy-creates a default row server-side
 * on first GET). Auto-saves on blur / pill click / toggle change via a
 * debounced PUT — no explicit Save button needed for routine edits, but
 * we surface one for confidence. Right-rail live preview updates as the
 * merchant types.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Send, Eye } from 'lucide-react';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import axiosInstance from '../../axiosConfig';
import './Settings.css';

// Capture rows + tone options live as plain arrays — easy to extend later
// without forking the markup.
const TONE_OPTIONS = [
  { key: 'friendly', label: 'Friendly' },
  { key: 'formal',   label: 'Formal' },
  { key: 'playful',  label: 'Playful' },
];

const DEFAULT_SETTINGS = {
  isEnabled: false,
  greetingMessage: '',
  fallbackToHuman: true,
  collectName: true,
  collectEmail: true,
  collectPhone: false,
  openOnLandingAfterMs: 15000,
  responseTimeHint: 'We typically reply within minutes',
  tone: 'friendly',
  brandColor: '#0d7463',
  botDisplayName: 'Reva',
  emailMerchantOnFirstMessage: true,
  pushMerchantOnFirstMessage: true,
  emailTranscriptOnClose: false,
  autoCreateCustomerFromChat: true,
  retentionDays: 365,
};

const Toggle = ({ checked, onChange, label, ariaLabel }) => (
  <label className={`scs-toggle ${checked ? 'is-on' : ''}`}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} aria-label={ariaLabel || label} />
    <span className="scs-toggle__track"><span className="scs-toggle__thumb" /></span>
    {label && <span className="scs-toggle__label">{label}</span>}
  </label>
);

const PillToggle = ({ active, children, onClick }) => (
  <button type="button" className={`scs-pill-toggle ${active ? 'is-on' : ''}`} onClick={onClick}>
    {children}
  </button>
);

// ---------- Live preview ---------------------------------------------------

const LivePreview = ({ settings, storeName, storeInitial }) => {
  const brand = settings.brandColor || '#0d7463';
  const greeting = (settings.greetingMessage || '').trim()
    || `Hi! Welcome to ${storeName || 'our store'}. Looking for something specific?`;
  const captureBits = [
    settings.collectName ? 'name' : null,
    settings.collectEmail ? 'email' : null,
    settings.collectPhone ? 'phone' : null,
  ].filter(Boolean);
  const sampleAsk = captureBits.length > 0
    ? `Before I help — what's your ${captureBits[0]}?`
    : 'Happy to help — what are you looking for?';

  return (
    <section className="scs-preview" aria-label="Widget preview">
      <div className="scs-preview__head">
        <span className="scs-preview__dot" aria-hidden /> Live preview
      </div>
      <div className="scs-mock">
        <div className="scs-mock__url">{storeName?.toLowerCase().replace(/\s+/g, '-') || 'your-store'}.userevstar.com</div>
        <div className="scs-mock__noise">
          <span /><span /><span /><span /><span /><span />
        </div>

        {settings.isEnabled ? (
          <div className="scs-pv" role="img" aria-label="Storefront chat preview">
            <div className="scs-pv__header" style={{ background: brand }}>
              <span className="scs-pv__avatar">{(settings.botDisplayName || 'R').charAt(0).toUpperCase()}</span>
              <div className="scs-pv__meta">
                <p className="scs-pv__title">{settings.botDisplayName || 'Reva'} · {storeName || 'Your store'}</p>
                <p className="scs-pv__sub">{settings.responseTimeHint || 'We typically reply within minutes'}</p>
              </div>
            </div>
            <div className="scs-pv__thread">
              <div className="scs-pv__msg scs-pv__msg--ai">{greeting}</div>
              <div className="scs-pv__msg scs-pv__msg--visitor" style={{ background: brand }}>Do you have it in green?</div>
              <div className="scs-pv__msg scs-pv__msg--ai">{sampleAsk}</div>
            </div>
            <div className="scs-pv__composer">
              <span className="scs-pv__input">Type your message…</span>
              <span className="scs-pv__send" style={{ background: brand }}><Send size={12} /></span>
            </div>
          </div>
        ) : (
          <div className="scs-pv__launcher" style={{ background: brand }} aria-hidden>
            <MessageCircle size={20} />
          </div>
        )}
      </div>
    </section>
  );
};

// ---------- Page ----------------------------------------------------------

const StorefrontChatSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [storeName, setStoreName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [error, setError] = useState(null);
  const lastSentRef = useRef(null);
  const debounceRef = useRef(null);

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, meRes] = await Promise.all([
          axiosInstance.get('/merchant/chatbot/settings'),
          axiosInstance.get('/merchants/me').catch(() => null),
        ]);
        if (cancelled) return;
        const fetched = settingsRes?.data?.settings || {};
        setSettings({ ...DEFAULT_SETTINGS, ...fetched });
        lastSentRef.current = { ...DEFAULT_SETTINGS, ...fetched };
        const store = meRes?.data?.store || meRes?.data?.user?.store;
        setStoreName(store?.name || '');
      } catch (err) {
        console.error('[ChatSettings] load failed:', err?.response?.data?.error || err?.message);
        setError('Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced auto-save — only fires when settings actually changed
  const save = useCallback(async (payload) => {
    setSaveStatus('saving');
    try {
      const res = await axiosInstance.put('/merchant/chatbot/settings', payload);
      const fresh = res?.data?.settings || payload;
      lastSentRef.current = { ...DEFAULT_SETTINGS, ...fresh };
      setSaveStatus('saved');
      setError(null);
      setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 1800);
    } catch (err) {
      console.error('[ChatSettings] save failed:', err?.response?.data?.error || err?.message);
      setError(err?.response?.data?.error || 'Failed to save');
      setSaveStatus('error');
    }
  }, []);

  const queueSave = useCallback((nextSettings) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const baseline = lastSentRef.current || {};
      const changed = Object.keys(nextSettings).some((k) => nextSettings[k] !== baseline[k]);
      if (!changed) return;
      save(nextSettings);
    }, 600);
  }, [save]);

  const update = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      queueSave(next);
      return next;
    });
  }, [queueSave]);

  // Immediate save for the master toggle so the merchant sees the result fast
  const updateImmediate = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      if (debounceRef.current) clearTimeout(debounceRef.current);
      save(next);
      return next;
    });
  }, [save]);

  const handleManualSave = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    save(settings);
  };

  // ---------- Render -----------------------------------------------------

  const statusEl = useMemo(() => {
    if (loading) return null;
    if (saveStatus === 'saving')        return <span className="scs-footer__status">Saving…</span>;
    if (saveStatus === 'saved')         return <span className="scs-footer__status is-saved">Saved</span>;
    if (saveStatus === 'error')         return <span className="scs-footer__status is-err">{error || 'Save failed'}</span>;
    return <span className="scs-footer__status">Changes save automatically</span>;
  }, [loading, saveStatus, error]);

  return (
    <Shell>
      <FeatureGate featureId="sales.storefront_chatbot" upgradeMessage="Upgrade to PRO or PREMIUM to set up the storefront AI chatbot.">
        <div className="scs-page">
          <header className="scs-header">
            <h1 className="scs-header__title">Storefront chat settings</h1>
            <p className="scs-header__sub">Configure how the AI greets shoppers, what it asks for, and how you get notified.</p>
            <nav className="scs-header__crumbs" aria-label="Storefront chat sections">
              <Link to="/storefront-chat" className="scs-header__crumb">Inbox</Link>
              <Link to="/storefront-chat/live" className="scs-header__crumb">Live</Link>
              <Link to="/storefront-chat/settings" className="scs-header__crumb is-active">Settings</Link>
            </nav>
          </header>

          <div className="scs-form">
            {/* Hero — master toggle */}
            <section className="scs-card scs-hero">
              <div className="scs-hero__row">
                <div className="scs-hero__body">
                  <h2 className="scs-hero__title">
                    {settings.isEnabled ? 'Chatbot is live on your storefront' : 'Turn on the chatbot'}
                  </h2>
                  <p className="scs-hero__sub">
                    {settings.isEnabled
                      ? 'Visitors see the launcher in the bottom-right corner. Edit any setting below and the live preview updates as you type.'
                      : 'Flip the toggle to start greeting visitors. You can configure tone, greeting, and what info to capture below.'}
                  </p>
                </div>
                <Toggle
                  checked={settings.isEnabled}
                  onChange={(v) => updateImmediate({ isEnabled: v })}
                  label={settings.isEnabled ? 'On' : 'Off'}
                  ariaLabel="Enable chatbot on storefront"
                />
              </div>
            </section>

            {/* Bot identity */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">Bot identity</h3>
                <p className="scs-card__sub">How the bot introduces itself in the chat header.</p>
              </div>

              <div className="scs-row">
                <label className="scs-label" htmlFor="scs-bot-name">
                  Bot name
                  <span className="scs-help">Shown as “{settings.botDisplayName || 'Reva'} · {storeName || 'Your store'}”</span>
                </label>
                <input
                  id="scs-bot-name"
                  type="text"
                  className="scs-input"
                  placeholder="Reva"
                  value={settings.botDisplayName || ''}
                  maxLength={40}
                  onChange={(e) => update({ botDisplayName: e.target.value })}
                />
              </div>

              <div className="scs-row">
                <span className="scs-label">Tone</span>
                <div className="scs-group">
                  {TONE_OPTIONS.map((t) => (
                    <PillToggle
                      key={t.key}
                      active={settings.tone === t.key}
                      onClick={() => update({ tone: t.key })}
                    >
                      {t.label}
                    </PillToggle>
                  ))}
                </div>
              </div>

              <div className="scs-row">
                <label className="scs-label" htmlFor="scs-brand-color">
                  Brand color
                  <span className="scs-help">Overrides your store theme color for this widget only.</span>
                </label>
                <div className="scs-color">
                  <div className="scs-color__swatch" style={{ background: settings.brandColor || '#0d7463' }}>
                    <input
                      id="scs-brand-color"
                      type="color"
                      value={(settings.brandColor || '#0d7463')}
                      onChange={(e) => update({ brandColor: e.target.value })}
                    />
                  </div>
                  <input
                    type="text"
                    className="scs-color__input"
                    value={settings.brandColor || ''}
                    onChange={(e) => update({ brandColor: e.target.value })}
                    placeholder="#0d7463"
                  />
                </div>
              </div>
            </section>

            {/* Greeting */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">Greeting</h3>
                <p className="scs-card__sub">First message the visitor sees. Leave blank to use the default for your store name.</p>
              </div>
              <div className="scs-row">
                <label className="scs-label" htmlFor="scs-greeting">
                  Greeting message
                </label>
                <textarea
                  id="scs-greeting"
                  className="scs-textarea"
                  placeholder={`Hi! Welcome to ${storeName || 'our store'}. Looking for something specific?`}
                  value={settings.greetingMessage || ''}
                  onChange={(e) => update({ greetingMessage: e.target.value })}
                  maxLength={500}
                />
              </div>
              <div className="scs-row">
                <label className="scs-label" htmlFor="scs-hint">
                  Response-time hint
                  <span className="scs-help">Appears under the bot name in the widget header.</span>
                </label>
                <input
                  id="scs-hint"
                  type="text"
                  className="scs-input"
                  placeholder="We typically reply within minutes"
                  value={settings.responseTimeHint || ''}
                  onChange={(e) => update({ responseTimeHint: e.target.value })}
                  maxLength={80}
                />
              </div>
            </section>

            {/* Lead capture */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">Lead capture</h3>
                <p className="scs-card__sub">The bot will ask naturally — never up front, never twice.</p>
              </div>
              <Toggle checked={settings.collectName}  onChange={(v) => update({ collectName: v })}  label="Ask for name" />
              <Toggle checked={settings.collectEmail} onChange={(v) => update({ collectEmail: v })} label="Ask for email" />
              <Toggle checked={settings.collectPhone} onChange={(v) => update({ collectPhone: v })} label="Ask for phone" />
            </section>

            {/* Behavior */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">Behavior</h3>
                <p className="scs-card__sub">When and how the widget opens itself.</p>
              </div>
              <div className="scs-row">
                <label className="scs-label" htmlFor="scs-open">
                  Auto-open delay
                  <span className="scs-help">
                    {settings.openOnLandingAfterMs >= 180000
                      ? 'Never'
                      : `${Math.round((settings.openOnLandingAfterMs || 15000) / 1000)}s after the page loads`}
                  </span>
                </label>
                <div className="scs-slider__row">
                  <input
                    id="scs-open"
                    type="range"
                    min="0"
                    max="180"
                    step="5"
                    className="scs-slider"
                    value={Math.min(180, Math.round((settings.openOnLandingAfterMs || 15000) / 1000))}
                    onChange={(e) => update({ openOnLandingAfterMs: parseInt(e.target.value, 10) * 1000 })}
                  />
                  <span className="scs-slider__val">
                    {(() => {
                      const secs = Math.min(180, Math.round((settings.openOnLandingAfterMs || 15000) / 1000));
                      if (secs >= 180) return 'Never';
                      if (secs === 0) return 'Immediate';
                      return `${secs}s`;
                    })()}
                  </span>
                </div>
              </div>
              <Toggle
                checked={settings.fallbackToHuman}
                onChange={(v) => update({ fallbackToHuman: v })}
                label="Fall back to the team when AI is unsure"
              />
            </section>

            {/* Notifications */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">Notifications</h3>
                <p className="scs-card__sub">We only ping you on the *first* message of a new conversation.</p>
              </div>
              <Toggle
                checked={settings.emailMerchantOnFirstMessage}
                onChange={(v) => update({ emailMerchantOnFirstMessage: v })}
                label="Email me on the first message of every new chat"
              />
              <Toggle
                checked={settings.pushMerchantOnFirstMessage}
                onChange={(v) => update({ pushMerchantOnFirstMessage: v })}
                label="Push notification on the first message"
              />
              <Toggle
                checked={settings.emailTranscriptOnClose}
                onChange={(v) => update({ emailTranscriptOnClose: v })}
                label="Email a chat transcript to me and the visitor when a conversation closes"
              />
            </section>

            {/* CRM linkage */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">CRM</h3>
                <p className="scs-card__sub">Chat leads can land in your Customers list automatically, tagged for follow-up.</p>
              </div>
              <Toggle
                checked={settings.autoCreateCustomerFromChat}
                onChange={(v) => update({ autoCreateCustomerFromChat: v })}
                label="Auto-add chat leads to my Customers (tagged 'Chatbot Lead')"
              />
            </section>

            {/* Retention */}
            <section className="scs-card">
              <div className="scs-card__head">
                <h3 className="scs-card__title">Retention</h3>
                <p className="scs-card__sub">Older conversations are pruned automatically.</p>
              </div>
              <div className="scs-row">
                <label className="scs-label" htmlFor="scs-retention">
                  Keep chats for
                  <span className="scs-help">{settings.retentionDays} days</span>
                </label>
                <input
                  id="scs-retention"
                  type="number"
                  min="7"
                  max="3650"
                  className="scs-input"
                  value={settings.retentionDays ?? 365}
                  onChange={(e) => update({ retentionDays: Math.max(7, Math.min(3650, parseInt(e.target.value, 10) || 365)) })}
                />
              </div>
            </section>

            {/* Auto-save status. Inline so it scrolls with content — no sticky
                bar fighting the dashboard footer. Only surfaces a manual
                retry button on save error. */}
            <div className="scs-status-row">
              {statusEl}
              {saveStatus === 'error' && (
                <button
                  type="button"
                  className="scs-save"
                  onClick={handleManualSave}
                  disabled={saveStatus === 'saving'}
                >
                  Retry save
                </button>
              )}
            </div>
          </div>

          <LivePreview settings={settings} storeName={storeName} />
        </div>
      </FeatureGate>
    </Shell>
  );
};

export default StorefrontChatSettings;
